import React, { useState, useEffect } from 'react';
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifButton,
  MotifFormField,
  MotifErrorMessage,
} from '@ey-xd/motif-react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import * as formHelpers from '../../scripts/formHelpers';
import * as formValidators from '../../scripts/formValidators';
import $ from 'jquery';
import { DateField } from '../shared/formFields';

const isValidDate = (date) => {
  return date instanceof Date && !isNaN(date.getTime());
};

// Custom validator to ensure End Date is after Election Date
const endDateAfterElectionDate = (value, allValues) => {
  if (!value || !allValues || !allValues.TRSElectionDate) return undefined;
  const endDate = new Date(value);
  const electionDate = new Date(allValues.TRSElectionDate);
  if (!isValidDate(endDate) || !isValidDate(electionDate)) return undefined;
  if (endDate < electionDate) {
    return 'The End Date cannot be earlier than the TRS Election Date.';
  }
  return undefined;
};

const AddEditTRSModal = ({
  reits,
  handleCloseForm,
  errorMessage,
  handleDismissErrorMessage,
  showAddEditTRSModal,
  toggleShowAddEditTRSModal,
  handleSubmit,
  onSave,
  validationMessage,
  clearValidationMessage,
  isEdit = false,
  initialValues = {},
  electionValidationMessage,
  endDateValidationMessage,
}) => {
  const modalHeader = isEdit ? 'Edit TRS' : 'Add New TRS';
  const [selectedREIT, setSelectedREIT] = useState(initialValues?.reitID || '');

  let scrollTop = () => {
    $('.ReactModal__Content').scrollTop(0);
  };

  let save = (e) => {
    e.preventDefault();
    scrollTop();
    handleSubmit((values) => onSave({ ...values }))();
  };

  useEffect(() => {
    if (initialValues && initialValues?.reitID) {
      setSelectedREIT(initialValues.reitID);
    }
  }, [initialValues]);

  const reitOptions = reits
    .map((r) => {
      return {
        key: r.reitid,
        value: r.reitid,
        text: `${r.reitShortName}_${r.reitTrackingID}`,
      };
    })
    .sort((a, b) => a.text.localeCompare(b.text)); // Sort alphabetically by the full display text (Reit Short Name and Tracking ID);

  return (
    <MotifModal
      variant="alt"
      show={showAddEditTRSModal}
      onClose={() => toggleShowAddEditTRSModal()}
      data-testid={isEdit ? 'divEditTRSModal' : 'divAddNewTRSModal'}
    >
      <MotifModalHeader
        data-testid={isEdit ? 'mdHdrEditTRS' : 'mdHdrAddNewTRS'}
      >
        <span className="ml-2">{modalHeader}</span>
      </MotifModalHeader>
      <form
        onSubmit={handleSubmit}
        id="addEditTRSForm"
        className="addEditTRSForm"
      >
        <MotifModalBody>
          {formHelpers.showSubmissionError(
            errorMessage,
            handleDismissErrorMessage
          )}
          <Field name="TRSID" type="hidden" component="input" label="" />
          <div className="row">
            <div className="field col-md-12" style={{ minHeight: '44px' }}>
              <MotifFormField>
                <Field
                  name="REITID"
                  label="REIT Name (Required)"
                  placeholder="Select one or more"
                  isRequiredField={true}
                  validate={[formValidators.required]}
                  options={reitOptions}
                  optionText={(r) => r.text}
                  optionValue={(r) => r.value}
                  component={formHelpers.SingleSelectComponent}
                  testingData={{ label: 'lblReits', div: 'divReits' }}
                  showSelectAllButton={true}
                  dataTestId="btnREITName"
                  disabled={isEdit}
                />
              </MotifFormField>
            </div>
          </div>
          <div className="row">
            <div className="field col-md-12">
              <MotifFormField>
                <Field
                  name="TRSName"
                  component={formHelpers.renderField}
                  type="text"
                  maxLength={255}
                  label="TRS Name"
                  dataTestId="txtTRSName"
                  isRequiredField={true}
                  validate={[
                    formValidators.required,
                    formValidators.maxLength50,
                  ]}
                  onChange={clearValidationMessage}
                />
                {(validationMessage && (
                  <MotifErrorMessage variant="error">
                    {validationMessage}
                  </MotifErrorMessage>
                )) ||
                  null}
              </MotifFormField>
            </div>
          </div>
          <div className="row">
            <div className="field col-md-6">
              <MotifFormField>
                <DateField
                  name="TRSElectionDate"
                  label="TRS Election Date (Required)"
                  placeholder="MM/DD/YYYY"
                  isRequiredField={true}
                  validate={[
                    formValidators.required,
                    formValidators.isValidDate,
                  ]}
                  dataTestId="txtElectionDate"
                  onChange={clearValidationMessage}
                />
                {(electionValidationMessage && (
                  <MotifErrorMessage variant="error">
                    {electionValidationMessage}
                  </MotifErrorMessage>
                )) ||
                  null}
              </MotifFormField>
            </div>
            <div className="field col-md-6">
              <MotifFormField>
                <DateField
                  name="TRSEndDate"
                  label="End Date"
                  placeholder="MM/DD/YYYY"
                  isRequiredField={false}
                  onChange={clearValidationMessage}
                  validate={[
                    formValidators.isValidDate,
                    endDateAfterElectionDate,
                  ]}
                  dataTestId="txtTRSEndDate"
                />
                {(endDateValidationMessage && (
                  <MotifErrorMessage variant="error">
                    {endDateValidationMessage}
                  </MotifErrorMessage>
                )) ||
                  null}
              </MotifFormField>
            </div>
          </div>
        </MotifModalBody>
        <MotifModalFooter>
          <MotifButton
            variant="secondary"
            type="button"
            onClick={handleCloseForm}
            className="right mt-3"
            data-testid="btnCancel"
          >
            Cancel
          </MotifButton>
          <MotifButton
            type="submit"
            onClick={save}
            className="right mt-3"
            data-testid="btnSave"
          >
            Save
          </MotifButton>
        </MotifModalFooter>
      </form>
    </MotifModal>
  );
};

AddEditTRSModal.propTypes = {
  handleSubmit: PropTypes.func,
  onSave: PropTypes.func,
  editTRS: PropTypes.func.isRequired,
  handleCloseForm: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    TRSID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    REITID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    TRSElectionDate: PropTypes.DateField,
    TRSEndDate: PropTypes.DateField,
    TRSName: PropTypes.string,
    REITName: PropTypes.string,
  }),
  errorMessage: PropTypes.string,
  submitting: PropTypes.bool,
  handleDismissErrorMessage: PropTypes.func,
  showAddEditTRSModal: PropTypes.bool.isRequired,
  toggleShowAddEditTRSModal: PropTypes.func.isRequired,
  electionValidationMessage: PropTypes.string,
  endDateValidationMessage: PropTypes.string,
};

export default reduxForm({ form: 'addEditTRSForm', enableReinitialize: true })(
  AddEditTRSModal
);
